import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ROUTES } from "@/config/routes";
import { emptyBulkExportFilters } from "@/domain/invoices/bulkExportFilters";
import type { PeriodShortcut } from "@/domain/invoices/datePeriodShortcuts";
import {
  type InvoiceFilterValues,
  validateInvoiceFilterValues,
} from "@/domain/invoices/invoiceFilterState";
import { BillingGateModal } from "@/features/billing/components/BillingGateModal";
import { usePlanAccess } from "@/features/billing/hooks/usePlanAccess";
import type { GatedCapability } from "@/features/billing/hooks/usePlanAccess";
import type { BillingGateReason } from "@/types/api";
import { BulkExportFiltersPanel } from "@/features/invoices/components/BulkExportFiltersPanel";
import { BulkExportModal } from "@/features/invoices/components/BulkExportModal";
import { useCustomers } from "@/features/invoices/hooks/useCustomers";
import { PageHeader } from "@/features/layout/components/PageHeader";

export function BulkCreditNotesExportPage() {
  const navigate = useNavigate();
  const { withGate } = usePlanAccess();
  const [billingGate, setBillingGate] = useState<{
    reason: BillingGateReason;
    capability: GatedCapability;
  } | null>(null);
  const [filters, setFilters] = useState<InvoiceFilterValues>(() =>
    emptyBulkExportFilters("last-month"),
  );
  const [activePeriod, setActivePeriod] = useState<PeriodShortcut | null>(
    "last-month",
  );
  const [modalOpen, setModalOpen] = useState(false);

  const customersQuery = useCustomers();
  const customers = customersQuery.data?.items ?? [];
  const filterIssues = validateInvoiceFilterValues(filters);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <PageHeader
        title="Bulk Export Credit Notes"
        lead="Set filters, then start a background ZIP export."
        breadcrumbSuffix="Bulk Export Credit Notes"
      />

      <div className="panel invoices-workspace mb-4 overflow-visible">
        <div className="workspace-section">
          <BulkExportFiltersPanel
            filters={filters}
            activePeriod={activePeriod}
            customers={customers}
            customersLoading={customersQuery.isLoading}
            customersError={null}
            onChange={(next) => {
              setFilters(next);
              setActivePeriod(null);
            }}
            onPeriodSelect={setActivePeriod}
            onReloadCustomers={() => void customersQuery.refetch()}
            variant="credit_note"
          />
          <div className="bulk-export-actions">
            <button
              type="button"
              className="btn-main shrink-0"
              disabled={filterIssues.length > 0}
              onClick={() => {
                const reason = withGate("bulk_export", () => setModalOpen(true));
                if (reason) {
                  setBillingGate({ reason, capability: "bulk_export" });
                }
              }}
            >
              Start export…
            </button>
          </div>
        </div>
      </div>

      <p className="text-sm text-[var(--muted)]">
        Preview rows first?{" "}
        <Link to={ROUTES.invoicesCreditNotes} className="link-quiet">
          Download Credit Notes
        </Link>
        {" · "}
        <Link to={ROUTES.exports} className="link-quiet">
          Export History
        </Link>
      </p>

      <BulkExportModal
        open={modalOpen}
        filters={filters}
        documentType="credit_note"
        onClose={() => setModalOpen(false)}
        onStarted={(jobId) => navigate(ROUTES.exportDetail(jobId))}
      />

      <BillingGateModal
        open={billingGate != null}
        reason={billingGate?.reason ?? null}
        capability={billingGate?.capability}
        onClose={() => setBillingGate(null)}
      />
    </div>
  );
}
